/* ==========================================================================
   Custom JS features
   ==========================================================================
   
   Added by Wilson from Galadrim
   Created: 30/04/2025
   
   Description:
   Custom JS features for the Dugas theme. This file contains general custom
   modifications and overrides to the base theme scripts.
   
   ========================================================================== */

// const { domainToUnicode } = require("url");

function getEffectiveLimit(atcButton) {
  const customerLimitRaw = atcButton.getAttribute("data-customer-limit");
  const inventoryQuantityRaw = atcButton.getAttribute("data-inventory-quantity");
  const inventoryPolicy = atcButton.getAttribute("data-inventory-policy");

  const customerLimit = customerLimitRaw && !isNaN(parseInt(customerLimitRaw, 10)) ? parseInt(customerLimitRaw, 10) : 0;
  const inventoryLimit =
    inventoryPolicy === "deny" && inventoryQuantityRaw && !isNaN(parseInt(inventoryQuantityRaw, 10))
      ? parseInt(inventoryQuantityRaw, 10)
      : 0;

  // If both limits are active, return the more restrictive (smaller) one.
  if (customerLimit > 0 && inventoryLimit > 0) {
    return Math.min(customerLimit, inventoryLimit);
  }
  // Otherwise, return whichever one is active.
  if (customerLimit > 0) {
    return customerLimit;
  }
  if (inventoryLimit > 0) {
    return inventoryLimit;
  }
  // Return 0 if no limits apply.
  return 0;
}

// Add to cart toast function begin
function showAddToCartToast(message, type = "success") {
  const toast = document.getElementById("add-to-cart-toast");
  if (!toast) return;

  const toastText = toast.querySelector(".add-to-cart-toast__text");
  const toastIcon = toast.querySelector(".add-to-cart-toast__icon");

  // Remove existing type classes
  toast.classList.remove("success", "failed");

  // Add the appropriate type class
  toast.classList.add(type);

  if (toastText) {
    if (message) {
      toastText.textContent = message;
    } else {
      // Default messages based on type
      if (type === "failed") {
        toastText.textContent = "Erreur lors de l'ajout au panier";
      } else {
        toastText.textContent = "Produit ajouté au panier";
      }
    }
  }

  // Update icon based on type
  if (toastIcon) {
    if (type === "failed") {
      toastIcon.innerHTML = `
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="16" fill="#dc3545"/>
          <path d="M12 12L20 20M20 12L12 20" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    } else {
      toastIcon.innerHTML = `
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="16" fill="#3CB371"/>
          <path d="M10 16.5L14 20.5L22 12.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }
  }

  toast.classList.add("show");
  setTimeout(function () {
    toast.classList.remove("show");
  }, 2500);
}

// Add a flag to prevent multiple openings
window.isCartDrawerOpening = false;

// Helper to clamp quantity
function clampQuantity(input, min, max) {
  let value = parseInt(input.value, 10) || min;
  if (!isNaN(max) && value > max) value = max;
  if (value < min) value = min;
  input.value = value;
  return value;
}

function updateQuantity(input, change) {
  const min = parseInt(input.getAttribute("min"), 10) || 1;
  const card = input.closest(".search-flydown--product-card");
  const plusBtn = card.querySelector(".qty-plus");
  const minusBtn = card.querySelector(".qty-minus");
  const customerLimit = parseInt(input.getAttribute("data-customer-limit"), 10);
  const inventoryQuantity = parseInt(card.getAttribute("data-inventory-quantity"), 10);
  const inventoryPolicy = card.getAttribute("data-inventory-policy");
  const inventoryManagement = card.getAttribute("data-inventory-management");
  let effectiveMax;
  if (inventoryManagement === "shopify" && inventoryPolicy === "deny") {
    effectiveMax = !isNaN(customerLimit) ? Math.min(customerLimit, inventoryQuantity) : inventoryQuantity;
  } else if (!isNaN(customerLimit)) {
    effectiveMax = customerLimit;
  } else {
    effectiveMax = inventoryQuantity || Infinity;
  }

  // Clamp value before proceeding
  const currentValue = parseInt(input.value, 10) || min;
  let newValue = currentValue + change;
  if (!isNaN(effectiveMax) && newValue > effectiveMax) newValue = effectiveMax;
  if (newValue < min) newValue = min;
  input.value = newValue;

  // Update button states
  if (plusBtn) {
    plusBtn.disabled = !isNaN(effectiveMax) && newValue >= effectiveMax;
    plusBtn.classList.toggle("quantity-selector__button--disabled", !isNaN(effectiveMax) && newValue >= effectiveMax);
  }
  if (minusBtn) {
    minusBtn.disabled = newValue <= min;
    minusBtn.classList.toggle("quantity-selector__button--disabled", newValue <= min);
  }

  // If value didn't change, do nothing
  if (newValue === currentValue) return;

  if (typeof window.updateCartDrawerConfirmButton === "function") {
    window.updateCartDrawerConfirmButton();
  }

  // Get the variant ID and line key
  const variantId = card.getAttribute("data-variant-id");
  const lineKey = card.getAttribute("data-cartitem-key");

  // AJAX update to Shopify cart
  if (!window.devMode) {
    fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        updates: {
          [lineKey]: newValue,
        },
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error("Failed to update cart");
        }
        return response.json();
      })
      .then(cart => {
        // Update line item price
        const priceElement = card.querySelector(".search-flydown--product-price");
        if (priceElement) {
          const updatedItem = cart.items.find(item => item.variant_id == parseInt(variantId));
          if (updatedItem) {
            let formattedPrice;
            try {
              if (
                window.Shopify &&
                typeof Shopify.formatMoney === "function" &&
                window.money_format &&
                window.money_format.indexOf("{{") === -1
              ) {
                formattedPrice = Shopify.formatMoney(updatedItem.final_line_price, window.money_format);
              } else {
                throw new Error("Shopify.formatMoney or money_format not available");
              }
            } catch (err) {
              formattedPrice = (updatedItem.final_line_price / 100).toLocaleString("fr-FR", {
                style: "currency",
                currency: "EUR",
              });
            }
            priceElement.textContent = formattedPrice;
          }
        }

        // Update cart total
        const totalElement = document.querySelector(".custom-cart-drawer__total span:last-child");
        if (totalElement) {
          let subtotal = cart.items_subtotal_price;
          if (cart.total_tax) {
            subtotal += cart.total_tax;
          }
          let formattedTotal;
          try {
            if (
              window.Shopify &&
              typeof Shopify.formatMoney === "function" &&
              window.money_format &&
              window.money_format.indexOf("{{") === -1
            ) {
              formattedTotal = Shopify.formatMoney(subtotal, window.money_format);
            } else {
              throw new Error("Shopify.formatMoney or money_format not available");
            }
          } catch (err) {
            formattedTotal = (subtotal / 100).toLocaleString("fr-FR", {
              style: "currency",
              currency: "EUR",
            });
          }
          totalElement.textContent = formattedTotal;
        }

        // Update cart summary in sidebar
        if (typeof updateCartSummary === "function") {
          updateCartSummary();
        }

        // Update button states based on effective max
        if (plusBtn) {
          plusBtn.disabled = !isNaN(effectiveMax) && newValue >= effectiveMax;
          plusBtn.classList.toggle(
            "quantity-selector__button--disabled",
            !isNaN(effectiveMax) && newValue >= effectiveMax
          );
        }
        if (minusBtn) {
          minusBtn.disabled = newValue <= min;
          minusBtn.classList.toggle("quantity-selector__button--disabled", newValue <= min);
        }
      })
      .catch(error => {
        // Clamp value to max after error
        input.value = clampQuantity(input, min, effectiveMax);
        if (plusBtn) {
          plusBtn.disabled = !isNaN(effectiveMax) && input.value >= effectiveMax;
          plusBtn.classList.toggle(
            "quantity-selector__button--disabled",
            !isNaN(effectiveMax) && input.value >= effectiveMax
          );
        }
        if (minusBtn) {
          minusBtn.disabled = input.value <= min;
          minusBtn.classList.toggle("quantity-selector__button--disabled", input.value <= min);
        }
        // Show appropriate error message using toast
        let errorMessage;
        if (error.message.includes("stock")) {
          errorMessage = "La quantité demandée n'est pas disponible en stock.";
        } else if (error.message.includes("limit")) {
          errorMessage = "Vous avez atteint la limite de commande pour ce produit.";
        } else {
          errorMessage = "Une erreur est survenue lors de la mise à jour du panier.";
        }
        showFailedToast(errorMessage);
      });
  }
}

// Handle redirect buttons begin
document.addEventListener("DOMContentLoaded", function () {
  const redirectButtons = document.querySelectorAll(".product-form--atc-button[data-redirect-url]");

  redirectButtons.forEach(button => {
    button.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      const redirectUrl = this.getAttribute("data-redirect-url");
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    });
  });
});

// Cart badge update function begin
function updateHeaderCartBadge() {
  const cart = Shopify.cart;
  const badge = document.getElementById("site-header-cart-badge");
  if (!badge) {
    return;
  }
  // const itemCount = cart && cart.item_count ? cart.item_count : 0;
  let itemCount = 0;
  cart.items.forEach(item => {
    if (item.sku != "DUTIES_CSS") itemCount += item.quantity;
  });
  badge.textContent = itemCount > 0 ? itemCount : "";
  badge.style.display = itemCount > 0 ? "flex" : "none";
}

// Click on cart icon to open cart drawer when cart is empty
document.addEventListener("DOMContentLoaded", function () {
  const cartLink = document.querySelector(".site-header-actions__cart a");

  if (cartLink) {
    cartLink.addEventListener("click", function (e) {
      e.preventDefault(); // Prevent navigation immediately

      if (Shopify.cart.item_count === 0) {
        const drawer = document.querySelector("[data-custom-cart-drawer]");
        const overlay = document.querySelector("[data-custom-cart-drawer-overlay]");
        if (drawer && overlay) {
          drawer.classList.add("active");
          overlay.classList.add("active");
          document.body.classList.add("drawer-active");
          document.body.style.overflow = "hidden";
        }
      } else {
        window.location.href = cartLink.href; // Redirect if cart is not empty
      }
    });
  }
});

// Cart summary update begin
function updateCartSummary() {
  // Fetch the updated cart section from Shopify with the section_id
  fetch(window.location.pathname + "?section_id=static-cart")
    .then(response => response.text())
    .then(html => {
      // Create a temporary DOM element to parse the returned HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      // Find the updated summary
      const newSummary = tempDiv.querySelector(".cartitems--container");
      const currentSummary = document.querySelector(".cartitems--container");
      if (newSummary && currentSummary) {
        currentSummary.innerHTML = newSummary.innerHTML;
      }

      creditCheckCartReload();
    })
    .then(() => {
      document.querySelector(".cartitems--container").classList.remove("loading");
    });
}

// Alphabet bar brands begin
document.addEventListener("DOMContentLoaded", function () {
  const alphabetBar = document.querySelector(".dugas-brands-alphabet");
  if (!alphabetBar) return;

  alphabetBar.querySelectorAll("span").forEach(function (span) {
    span.addEventListener("click", function () {
      // Remove custom active class from all
      alphabetBar.querySelectorAll("span").forEach(s => s.classList.remove("dugas-brands-alphabet-active"));
      // Add custom active class to clicked
      span.classList.add("dugas-brands-alphabet-active");
      // Scroll
      const letter = span.textContent.trim();
      const target = document.getElementById("brands-" + letter);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  });
});

// Notifications B2B begin
document.addEventListener("DOMContentLoaded", function () {
  const toggleBtn = document.querySelector(".site-header-actions__notifications");
  const notifBox = document.getElementById("b2b-notifications");
  const tabs = document.querySelectorAll("#notification-tabs span");
  const items = document.querySelectorAll(".notification-item");
  const redDot = document.getElementById("notification-red-dot");

  function checkRedDotVisibility() {
    const visibleDots = document.querySelectorAll(".notification-unread-dot:not(.hidden)");
    const anyVisible = Array.from(visibleDots).some(
      dot => window.getComputedStyle(dot).visibility !== "hidden" && dot.offsetParent !== null
    );

    if (!anyVisible && redDot) {
      redDot.remove();
    }
  }

  // Toggle box open/close
  if (toggleBtn && notifBox) {
    notifBox.style.display = "none";

    toggleBtn.addEventListener("click", function (e) {
      e.stopPropagation();
      const isVisible = notifBox.style.display === "block";
      notifBox.style.display = isVisible ? "none" : "block";
      toggleBtn.classList.toggle("b2b-notifications-active", !isVisible);

      // Check for red dot after display is toggled on
      if (!isVisible) {
        setTimeout(checkRedDotVisibility, 100);
      }
    });

    document.addEventListener("click", function (e) {
      if (!notifBox.contains(e.target) && !toggleBtn.contains(e.target)) {
        notifBox.style.display = "none";
        toggleBtn.classList.remove("b2b-notifications-active");
      }
    });
  }

  // Tabs filter
  tabs.forEach(tab => {
    tab.addEventListener("click", function () {
      const filter = this.dataset.filter;
      tabs.forEach(t => t.classList.remove("active"));
      this.classList.add("active");
      items.forEach(item => {
        item.style.display = filter === "all" || item.dataset.type === filter ? "block" : "none";
      });
      // Re-check red dot when filtering
      setTimeout(checkRedDotVisibility, 100);
    });
  });

  // Click to mark notification as seen
  document.querySelectorAll(".notification-item").forEach(item => {
    item.addEventListener("click", function () {
      const blueDot = this.querySelector(".notification-unread-dot");
      if (blueDot) blueDot.classList.add("hidden");

      // Re-check after marking
      checkRedDotVisibility();

      const notifId = this.dataset.id;
      fetch("/apps/mark-notification-as-seen", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ notification_id: notifId }),
      }).catch(error => {
        console.error("Error calling webhook:", error);
      });
    });
  });
});

// Notify me button begin
document.addEventListener("DOMContentLoaded", function () {
  const notifyBtn = document.querySelector("button:has(#notify-me-btn)");

  if (notifyBtn) {
    notifyBtn.addEventListener("click", function () {
      const productId = parseInt(this.getAttribute("data-product-id")) || null;
      const customerId = parseInt(this.dataset.customerId);
      const customerEmail = this.dataset.customerEmail;

      fetch("/apps/register-stock-alert", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          product_id: productId,
          customer_id: customerId || null,
          email: customerEmail || null,
        }),
      })
        .then(response => {
          if (response.ok) {
            alert("✅ Vous serez alerté quand ce produit sera de nouveau en stock.");
          } else {
            alert("❌ Une erreur est survenue. Merci de réessayer.");
          }
        })
        .catch(error => {
          console.error("Erreur lors de l'envoi :", error);
        });
    });
  }
});

// Quantity selector limit begin
document.addEventListener("DOMContentLoaded", function () {
  document.querySelectorAll("[data-quantity-selector]").forEach(selector => {
    const input = selector.querySelector("[data-quantity-input]");
    const plusBtn = selector.querySelector("[data-quantity-plus]");
    const minusBtn = selector.querySelector("[data-quantity-minus]");
    let max = parseInt(input.getAttribute("max"), 10);
    // Fallback: check data-customer-limit on any ancestor if max is not set
    if (isNaN(max)) {
      let ancestor = selector;
      while (ancestor && !ancestor.hasAttribute("data-customer-limit")) {
        ancestor = ancestor.parentElement;
      }
      if (ancestor && ancestor.hasAttribute("data-customer-limit")) {
        const parentLimit = ancestor.getAttribute("data-customer-limit");
        if (parentLimit && !isNaN(parseInt(parentLimit, 10))) {
          max = parseInt(parentLimit, 10);
        }
      }
    }

    // Only apply this non-standard logic for product form (not cart, grid, etc)
    const isProductForm = !!selector.closest("form[data-product-form]");

    function clampValue() {
      let value = parseInt(input.value, 10) || 1;
      if (isProductForm) {
        if (!isNaN(max) && value > max + 1) value = max + 1;
      } else {
        if (!isNaN(max) && value > max) value = max;
      }
      if (value < 1) value = 1;
      input.value = value;
    }

    function updateButtons() {
      const value = parseInt(input.value, 10) || 1;
      if (isProductForm) {
        plusBtn.disabled = !isNaN(max) && value >= max;
        plusBtn.classList.toggle("quantity-selector__button--disabled", !isNaN(max) && value >= max);
      } else {
        plusBtn.disabled = !isNaN(max) && value >= max;
        plusBtn.classList.toggle("quantity-selector__button--disabled", !isNaN(max) && value >= max);
      }
      minusBtn.disabled = value <= 1;
      minusBtn.classList.toggle("quantity-selector__button--disabled", value <= 1);
    }

    if (!isNaN(max)) {
      input.addEventListener("input", function () {
        clampValue();
        updateButtons();
      });
      input.addEventListener("change", function () {
        clampValue();
        updateButtons();
      });
      plusBtn.addEventListener("click", function () {
        setTimeout(function () {
          clampValue();
          updateButtons();
        }, 0);
      });
      minusBtn.addEventListener("click", function () {
        setTimeout(function () {
          clampValue();
          updateButtons();
        }, 0);
      });
      clampValue();
      updateButtons();
    }
  });
});

// Product form limit message and ATC button disable logic
function forceShowMessage(limitMessage, quantityError, forceTries) {
  limitMessage.style.setProperty("display", "flex", "important");
  if (quantityError) {
    quantityError.style.display = "none";
  }
  limitMessage.style.color = "#B42318";
  if (++forceTries < 10) setTimeout(() => forceShowMessage(limitMessage, quantityError, forceTries), 50);
}

document.addEventListener("DOMContentLoaded", function () {
  const atcButtons = document.querySelectorAll(".product-form--atc-button[data-customer-limit]");
  atcButtons.forEach(function (atcButton, idx) {
    const form = atcButton.closest("form[data-product-form]");
    if (!form) {
      return;
    }
    const limitMessage = form.querySelector("#product-limit-message");
    const quantityInputs = form.querySelectorAll("[data-quantity-input]");
    const quantityInput = quantityInputs[0];
    const customerLimit = parseInt(atcButton.getAttribute("data-customer-limit"), 10);
    if (!limitMessage || !quantityInput || isNaN(customerLimit)) {
      return;
    }

    function updateLimitState() {
      const value = parseInt(quantityInput.value, 10) || 1;
      const quantityError = form.querySelector(".quantity-selector__error-message");

      if (value >= customerLimit + 1) {
        // Disable ATC at limit+1
        const forceTries = 0;
        forceShowMessage(limitMessage, quantityError, forceTries);
        atcButton.classList.add("disabled", "limit-disabled");
        atcButton.setAttribute("disabled", "disabled");
      } else {
        limitMessage.style.setProperty("display", "none", "important");
        atcButton.classList.remove("disabled", "limit-disabled");
        atcButton.removeAttribute("disabled");
      }
    }

    // Run on page load
    updateLimitState();

    // Poll for value changes
    let lastValue = quantityInput.value;
    setInterval(function () {
      if (quantityInput.value !== lastValue) {
        lastValue = quantityInput.value;
        updateLimitState();
      }
    }, 100);

    quantityInputs.forEach(function (input, i) {
      input.addEventListener("input", function () {
        updateLimitState();
      });
      input.addEventListener("change", function () {
        updateLimitState();
      });
    });
  });
});

function preventConfirm(e) {
  e.preventDefault();
  e.stopPropagation();
  return false;
}

// Helper to re-apply volume pricing highlights to all cart items.
function highlightAllCartItemTiers() {
  // Exit if the global function from the B2B app isn't available.
  if (typeof window.highlightCurrentTier !== "function") {
    return;
  }

  // Calculate total cart quantity
  let totalQty = 0;

  if (typeof window.getTotalCartItemCount === "function") {
    totalQty = window.getTotalCartItemCount();
  } else {
    // Fallback: calculate manually if function is not available
    document.querySelectorAll(".cart-item").forEach(item => {
      const qtyInput = item.querySelector(".quantity-selector__input");
      if (qtyInput) {
        const qty = parseInt(qtyInput.value, 10) || 0;
        totalQty += qty;
      }
    });
  }

  document.querySelectorAll(".cart-item").forEach(card => {
    const grid = card.querySelector(".b2b-pricing-grid");

    if (grid) {
      window.highlightCurrentTier(grid, totalQty);
    }
  });
}

// Utility: refresh the cart page section using Shopify's section rendering API
function refreshCartPageSection() {
  const cartContainer = document.querySelector(".cartitems--container");
  if (cartContainer) {
    cartContainer.classList.add("is-updating");
  }

  return fetch(window.location.pathname + "?section_id=static-cart")
    .then(response => response.text())
    .then(html => {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      const newCartContainer = tempDiv.querySelector(".cartitems--container");
      const currentCartContainer = document.querySelector(".cartitems--container");
      const newCartTitle = tempDiv.querySelector(".cart-title");
      const currentCartTitle = document.querySelector(".cart-title");

      if (newCartContainer && currentCartContainer) {
        currentCartContainer.innerHTML = newCartContainer.innerHTML;
      }

      if (newCartTitle && currentCartTitle) {
        currentCartTitle.innerHTML = newCartTitle.innerHTML;
      }

      // Re-initialize JS logic for the new elements
      if (typeof initializeCustomLogic === "function") {
        initializeCustomLogic();
      }

      // This is the fix: Dispatch an event that the theme's own scripts can listen for,
      // prompting them to re-initialize things like the remove button handler.
      const cartSection = document.querySelector("#shopify-section-static-cart");
      if (cartSection) {
        cartSection.dispatchEvent(new CustomEvent("shopify:section:load", { bubbles: true }));
      }

      // This is the fix: Re-apply highlights to all items after the refresh.
      highlightAllCartItemTiers();

      /* --- PROMOS -------------------------------------------------- */
      // 1. Réinitialise le flag uniquement pour les conteneurs vides
      document.querySelectorAll(".custom-promotions-container").forEach(c => {
        if (c.innerHTML.trim() === "") {
          c.dataset.promoInit = "0";
        }
      });
      // 2. Déclenche la reconstruction/refresh (une seule fois)
      document.dispatchEvent(new Event("promos:refresh"));
      /* -------------------------------------------------------------- */
    })
    .finally(() => {
      if (cartContainer) {
        cartContainer.classList.remove("is-updating");
      }
    });
}

/* -------------------------------------------------------------
   Relaunch after each reconstruction of the cart section
   ------------------------------------------------------------- */
document.addEventListener("DOMContentLoaded", () => {
  checkAllCartItemsErrorState(); // Check on page load.
});
document.addEventListener("cart:updated", () => {
  checkAllCartItemsErrorState(); // Check after any cart update.
});
document.addEventListener("shopify:section:load", e => {
  if (e.target.querySelector && e.target.querySelector(".cartitems--container")) {
    checkAllCartItemsErrorState(); // Check on section reload.
  }
});

// New function to check the error state of all cart items.
function checkAllCartItemsErrorState() {
  document.querySelectorAll(".cart-item[data-cartitem-key]").forEach(itemRow => {
    const input = itemRow.querySelector(".quantity-selector__input");
    if (!input) return;

    const selector = input.closest("[data-quantity-selector]");
    const errorMsg = selector?.querySelector("[data-quantity-error-message]");
    const wrapper = selector?.closest(".cart-item--qty-block");

    if (!errorMsg || !wrapper) return;

    const max = parseInt(input.getAttribute("max"), 10);
    // If there's no max, ensure the error is hidden.
    if (isNaN(max)) {
      errorMsg.classList.remove("show");
      wrapper.classList.remove("has-active-error");
      return;
    }

    const qty = parseInt(input.value, 10);
    // We only HIDE the error here. Showing the error is handled by the direct user interaction (the click handler).
    // This prevents an error on item A from re-appearing when the user interacts with item B.
    if (qty < max) {
      errorMsg.classList.remove("show");
      wrapper.classList.remove("has-active-error");
    }
  });
}

// Utility: refresh the cart section using Shopify's section rendering API
function refreshCartSection() {
  window.location.reload();
}

// Cart page: clamp all line item quantities to their limit
function clampCartPageQuantitiesAndButtons() {
  const container = document.querySelector(".cartitems--container");
  if (!container) return;

  if (window.cartQuantityListener) {
    container.removeEventListener("click", window.cartQuantityListener, true);
  }

  window.cartQuantityListener = e => {
    const selector = e.target.closest("[data-quantity-selector]");

    // Only proceed if it's a plus button AND has our specific allocation flag.
    if (!e.target.matches("[data-quantity-plus]") || !selector || !selector.hasAttribute("data-is-allocated-limit")) {
      return;
    }

    const input = selector.querySelector("input");
    const maxAttr = input.getAttribute("max");
    const max = maxAttr ? parseInt(maxAttr, 10) : null;
    const current = parseInt(input.value, 10);

    if (max !== null && current >= max) {
      e.stopPropagation();
      e.preventDefault();

      const errorMessage = selector.querySelector("[data-quantity-error-message]");
      const wrapper = selector.closest(".cart-item--qty-block");

      if (errorMessage && wrapper) {
        errorMessage.classList.add("show");
        wrapper.classList.add("has-active-error");
        setTimeout(() => {
          errorMessage.classList.remove("show");
          wrapper.classList.remove("has-active-error");
        }, 3000);
      }
    }
  };

  container.addEventListener("click", window.cartQuantityListener, true);
}

//  Dispatch cart:updated after AJAX cart updates
(function () {
  // Patch fetch for /cart/add.js and /cart/change.js to dispatch cart:updated
  const origFetch = window.fetch;
  window.fetch = function () {
    const fetchArgs = arguments;
    const url = (fetchArgs[0] && fetchArgs[0].toString()) || "";

    // Create a promise from the original fetch call
    const fetchPromise = origFetch.apply(this, arguments);

    // If it's a cart update, attach a .then() to dispatch our custom event
    if (url.includes("/cart/add.js") || url.includes("/cart/change.js") || url.includes("/cart/update.js")) {
      fetchPromise.then(function (response) {
        // We need to clone the response to read it here and still have it available for the original caller
        if (response.ok) {
          response
            .clone()
            .json()
            .then(cart => {
              document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: cart } }));
            })
            .catch(e => console.error("Error parsing cart response for event dispatch:", e));
        }
        return response; // Pass the original response along
      });
    }

    return fetchPromise; // Return the original promise immediately
  };
})();

/*
  document.addEventListener('cart:updated', async function() {
    return;

    if(document.querySelector('body').classList.contains('template-cart')){
      const selector = 'form[action="/cart"] .cartitems'
      const cart = document.querySelector(selector);

      try{
        const response = await fetch(document.querySelector('form[action="/cart"]').dataset.url);
        if(!response.ok) throw new Error(response.status);

        const newCart = new DOMParser().parseFromString(await response.text(), 'text/html');

        cart.innerHTML = newCart.querySelector(selector).innerHTML;

      } catch(err) {
        console.warn(err);
      }
    }
  })
*/

// Manual Price Update for Allocated Cart Items
document.addEventListener("cart:updated", function (event) {
  // if (!document.querySelector("body.template-cart")) {
  //   return; // Only run on the main cart page
  // }
  // const cart = event.detail.cart;
  // if (!cart || !cart.items) return;
  // Update prices for all items, this is more robust
  /*
  cart.items.forEach(item => {
    // Find all matching item elements on the page (could be in regular or allocated lists)
    const itemElements = document.querySelectorAll(`.cart-item[data-cartitem-key="${item.key}"]`);

    itemElements.forEach(itemElement => {
      const priceElement = itemElement.querySelector(".cart-item--price");
      if (priceElement) {
        let formattedPrice;
        const moneyFormat = window.money_format || "{{ shop.money_format | strip_html }}";
        try {
          if (window.Shopify && typeof Shopify.formatMoney === "function" && moneyFormat.includes("{{")) {
            // Fallback for when liquid isn't rendered, use a manual but consistent format
            formattedPrice = `${(item.final_line_price / 100).toFixed(2).replace(".", ",")} €`;
          } else if (window.Shopify && typeof Shopify.formatMoney === "function") {
            formattedPrice = Shopify.formatMoney(item.final_line_price, moneyFormat);
          } else {
            // Absolute fallback
            formattedPrice = `${(item.final_line_price / 100).toFixed(2).replace(".", ",")} €`;
          }
          priceElement.innerHTML = formattedPrice;
        } catch (e) {
          console.error("Error formatting money:", e);
          // Fallback in case of error
          priceElement.innerHTML = `${(item.final_line_price / 100).toFixed(2).replace(".", ",")} €`;
        }
      }
    });
  });
  */
  // Also, trigger a refresh of the summary block
  // if (typeof updateCartSummary === "function") {
  //   updateCartSummary();
  // }
});

document.addEventListener("cart:newcart", () => {
  if (document.querySelector("body.template-cart")) {
    updateCartSummary();
  }
  updateHeaderCartBadge();
});

// Trigger Cart Update on Quantity Change
document.addEventListener("DOMContentLoaded", function () {
  const cartForm = document.querySelector('form[action="/cart"]');
  if (!cartForm) return;

  // Debounce function
  const debounce = (callback, wait) => {
    let timeoutId = null;
    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        callback.apply(null, args);
      }, wait);
    };
  };

  // This function will collect all quantities and send a single update
  const handleCartUpdate = debounce(() => {
    const updates = {};
    const inputs = cartForm.querySelectorAll(".quantity-selector__input");
    inputs.forEach(input => {
      const itemRow = input.closest(".cart-item");
      if (itemRow) {
        const key = itemRow.getAttribute("data-cartitem-key");
        if (key) {
          updates[key] = input.value;
        }
      }
    });

    if (Object.keys(updates).length > 0) {
      cartForm.classList.add("is-loading");
      fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ updates: updates }),
      }).finally(() => {
        cartForm.classList.remove("is-loading");
      });
    }
  }, 500); // Debounce by 500ms

  // Listen for changes on the form
  cartForm.addEventListener("change", event => {
    if (event.target.matches(".quantity-selector__input")) {
      handleCartUpdate();
    }
  });
});

// MutationObserver for robust cart state checking
document.addEventListener("DOMContentLoaded", function () {
  const cartItemsContainer = document.querySelector(".cartitems"); // Main container for cart items
  const allocatedSectionContainer = document.body; // Watch body for appearance/disappearance of .allocated-products-section
  const b2bAlertsContainer = document.body; // Watch body for appearance/disappearance of .alert-unpaid-invoice

  const callback = function (mutationsList, observer) {
    for (const mutation of mutationsList) {
      if (typeof window.updateCartPageCheckoutButton === "function") {
        window.updateCartPageCheckoutButton();
      }
      return; // Re-check once per batch of mutations
    }
  };

  const observer = new MutationObserver(callback);
  const config = { childList: true, subtree: true, attributes: false };

  if (cartItemsContainer) {
    observer.observe(cartItemsContainer, config);
  }
  observer.observe(allocatedSectionContainer, {
    childList: true,
    subtree: true,
  });
});

document.addEventListener("DOMContentLoaded", function () {
  const cartItemsContainer = document.querySelector(".cartitems");
  const mainWatchTarget = document.body;

  const callback = function (mutationsList, observer) {
    for (const mutation of mutationsList) {
      if (typeof window.updateCartPageCheckoutButton === "function") {
        window.updateCartPageCheckoutButton();
      }
      return;
    }
  };

  const observer = new MutationObserver(callback);
  const config = { childList: true, subtree: true };

  if (cartItemsContainer) {
    observer.observe(cartItemsContainer, config);
  }

  observer.observe(mainWatchTarget, config);
});

// Move banner to <body>
(function () {
  // Helper: Move only visible, open banner to <body>
  function moveBannerIfOpen(banner) {
    if (banner && banner.parentNode !== document.body && banner.getAttribute("data-animation-state") === "open") {
      document.body.appendChild(banner);
    }
  }

  // Watch for new banners being added
  const bannerCreationObserver = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1 && node.classList.contains("message-banner--container")) {
          // Watch for animation state changes on this banner
          const banner = node;
          const attrObserver = new MutationObserver(() => moveBannerIfOpen(banner));
          attrObserver.observe(banner, {
            attributes: true,
            attributeFilter: ["data-animation-state"],
          });
          // In case it's already open
          moveBannerIfOpen(banner);
        }
      });
    });
  });
  bannerCreationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // On page load, handle any already present banner
  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll(".message-banner--container").forEach(banner => {
      const attrObserver = new MutationObserver(() => moveBannerIfOpen(banner));
      attrObserver.observe(banner, {
        attributes: true,
        attributeFilter: ["data-animation-state"],
      });
      moveBannerIfOpen(banner);
    });
  });
})();

function showSuccessToast(message) {
  // Réutilise le toast centralisé
  if (typeof showAddToCartToast === "function") {
    showAddToCartToast(message, "success");
  }
}

function showFailedToast(message) {
  // Utilise le toast centralisé avec le style d'échec
  if (typeof showAddToCartToast === "function") {
    showAddToCartToast(message, "failed");
  }
}

/*
 * Exemples d'utilisation des toasts :
 *
 * // Toast de succès avec message par défaut
 * showAddToCartToast();
 *
 * // Toast de succès avec message personnalisé
 * showAddToCartToast("Produit ajouté avec succès !");
 *
 * // Toast d'échec avec message par défaut
 * showAddToCartToast(null, "failed");
 *
 * // Toast d'échec avec message personnalisé
 * showAddToCartToast("Stock insuffisant", "failed");
 *
 * // Utilisation des fonctions helper
 * showSuccessToast("Action réussie");
 * showFailedToast("Action échouée");
 */

//  Product Page Quantity Clamping
function clampProductPageQuantity() {
  const form = document.querySelector("form[data-product-form]");
  if (!form) return;

  const input = form.querySelector("[data-quantity-input]");
  const atcButton = form.querySelector(".product-form--atc-button[data-customer-limit]");

  if (!input || !atcButton) return;

  const maxAttr = atcButton.getAttribute("data-customer-limit");
  // Exit if the limit is not yet set or is not a number
  if (!maxAttr || isNaN(parseInt(maxAttr, 10))) return;

  const max = parseInt(maxAttr, 10);
  const selector = input.closest("[data-quantity-selector]");
  if (!selector) return;

  const plusBtn = selector.querySelector("[data-quantity-plus]");
  const minusBtn = selector.querySelector("[data-quantity-minus]");

  let value = parseInt(input.value, 10) || 1;

  // Clamp the value if it exceeds the max
  if (value > max) {
    input.value = max;
    value = max;
  }

  // Update plus/minus button states
  if (plusBtn) {
    plusBtn.disabled = value >= max;
    plusBtn.classList.toggle("quantity-selector__button--disabled", value >= max);
  }
  if (minusBtn) {
    minusBtn.disabled = value <= 1;
    minusBtn.classList.toggle("quantity-selector__button--disabled", value <= 1);
  }
}

function initializeProductPageQuantityClamping() {
  const form = document.querySelector("form[data-product-form]");
  if (!form) return;

  // Run on any input or change event on the form
  form.addEventListener("input", clampProductPageQuantity);
  form.addEventListener("change", clampProductPageQuantity);

  // Re-run when the theme changes the variant
  form.addEventListener("variant:change", () => {
    // A small delay ensures the theme's script has updated the DOM
    setTimeout(clampProductPageQuantity, 50);
  });

  // Run once on load just in case
  clampProductPageQuantity();
}

document.addEventListener("DOMContentLoaded", initializeProductPageQuantityClamping);
document.addEventListener("shopify:section:load", initializeProductPageQuantityClamping);

// Cart and Product page: clamp all line item quantities to their limit
function clampQuantitiesAndButtons() {
  const selectors = document.querySelectorAll(".quantity-selector, .product-form--quantity-selector");

  selectors.forEach(selector => {
    const input = selector.querySelector(
      'input[type="number"][name="quantity"], input[type="number"][name="updates[]"], .qty-input'
    );
    if (!input) return;

    const max = parseInt(input.getAttribute("max"), 10);
    if (isNaN(max)) return;

    const currentValue = parseInt(input.value, 10);

    if (currentValue > max) {
      input.value = max;
      // Set a global flag to indicate a clamp just happened.
      window.quantityWasClamped = true;
    }

    // This part handles the +/- buttons for the main quantity selector
    const minusButton = selector.querySelector("[data-quantity-minus]");
    const plusButton = selector.querySelector("[data-quantity-plus]");

    if (minusButton) {
      minusButton
        .closest("[data-button-wrapper]")
        .classList.toggle("quantity-selector__button-wrapper--disabled", currentValue <= 1);
    }
    if (plusButton) {
      plusButton
        .closest("[data-button-wrapper]")
        .classList.toggle("quantity-selector__button-wrapper--disabled", currentValue >= max);
    }

    // This part handles the ATC button on the product page
    const form = selector.closest("form[data-product-form]");
    if (form) {
      const atcButton = form.querySelector(".product-form--atc-button");
      if (atcButton) {
        atcButton.disabled = currentValue > max;
      }
    }
  });
}

function initializeNativeBannerFix() {
  // Disconnect any previous observer to prevent duplicates
  if (window.nativeBannerObserver) {
    window.nativeBannerObserver.disconnect();
  }

  const handleMutation = mutationsList => {
    for (const mutation of mutationsList) {
      if (mutation.type !== "childList") continue;

      mutation.addedNodes.forEach(node => {
        // Is the added node the banner container? (Selector from theme's JS)
        if (node.nodeType !== 1 || !node.matches("[data-banner][data-animation-as-appear]")) return;

        const bannerMessageElement = node.querySelector(".banner--message");
        const bannerText = bannerMessageElement ? bannerMessageElement.textContent : "";

        // Does the banner have the specific text we want to control?
        if (bannerText.includes("La quantité maximale") || /all available units/i.test(bannerText)) {
          // Hide the banner immediately to prevent any flicker while we verify.
          node.style.display = "none";

          // Defer the verification slightly to ensure the DOM is fully updated after the cart change.
          setTimeout(() => {
            let isErrorReal = false;
            // Check all quantity inputs in the cart drawer and on the main cart page.
            const quantityInputs = document.querySelectorAll(
              ".custom-cart-drawer .qty-input, .cart-item .quantity-selector__input, .cart-item__quantity-wrapper input"
            );

            for (const input of quantityInputs) {
              const max = parseInt(input.getAttribute("max"), 10);
              const value = parseInt(input.value, 10);

              // If an item's quantity is at or over its limit, the error is real.
              if (!isNaN(max) && !isNaN(value) && value >= max) {
                isErrorReal = true;
                break; // A real error was found, no need to check other items.
              }
            }

            if (isErrorReal) {
              // This is a legitimate error. Make the banner visible again.
              node.style.display = "";
            } else {
              // This is a false positive caused by the theme bug. Remove it.
              node.remove();
            }
          }, 50); // A 50ms delay is usually sufficient.
        }
      });
    }
  };

  const observer = new MutationObserver(handleMutation);
  observer.observe(document.body, { childList: true, subtree: true });

  // Store the observer on the window object so it can be managed.
  window.nativeBannerObserver = observer;
}

function _updateProductPageQuantityUI() {
  const productForm = document.querySelector("form[data-product-form]");
  if (!productForm) return;
  const selector = productForm.querySelector(".quantity-selector");
  if (!selector) return;
  const input = selector.querySelector(".quantity-selector__input");
  const plusBtn = selector.querySelector("[data-quantity-plus]");
  const minusBtn = selector.querySelector("[data-quantity-minus]");
  const errorMessage = selector.querySelector("[data-quantity-error-message]");
  if (!input || !plusBtn || !minusBtn || !errorMessage) return;

  const max = parseInt(input.getAttribute("max"), 10) || Infinity;
  let value = parseInt(input.value, 10) || 1;

  // Clamp the value if it exceeds the max
  if (value > max) {
    value = max;
    input.value = value;
  }

  const limitReached = value >= max;
  errorMessage.style.display = limitReached ? "block" : "none";

  const plusWrapper = plusBtn.closest("[data-button-wrapper]");
  if (plusWrapper) {
    plusWrapper.classList.toggle("quantity-selector__button-wrapper--disabled", limitReached);
  } else {
    plusBtn.disabled = limitReached;
  }

  const minusWrapper = minusBtn.closest("[data-button-wrapper]");
  if (minusWrapper) {
    minusWrapper.classList.toggle("quantity-selector__button-wrapper--disabled", value <= 1);
  } else {
    minusBtn.disabled = value <= 1;
  }
}

function _resetProductPageQuantityInput() {
  const productPageForm = document.querySelector("form[data-product-form]");
  if (productPageForm) {
    const quantityInput = productPageForm.querySelector(".quantity-selector__input");
    if (quantityInput) {
      quantityInput.value = 1;
    }
    _updateProductPageQuantityUI();
  }
}

function _initializeCartDrawerObserver() {
  const cartDrawer = document.querySelector("[data-custom-cart-drawer]");
  if (!cartDrawer) return;

  const observer = new MutationObserver(mutationsList => {
    for (const mutation of mutationsList) {
      if (mutation.type === "attributes" && mutation.attributeName === "class") {
        const wasActive = mutation.oldValue.includes("active");
        const isActive = cartDrawer.classList.contains("active");
        if (wasActive && !isActive) {
          _resetProductPageQuantityInput();
        }
      }
    }
  });

  observer.observe(cartDrawer, {
    attributes: true,
    attributeOldValue: true,
  });
}

function _initializeProductPageQuantityListeners() {
  const productForm = document.querySelector("form[data-product-form]");
  if (productForm) {
    _updateProductPageQuantityUI();
    productForm.addEventListener("click", e => {
      if (e.target.closest(".quantity-selector__button")) {
        setTimeout(_updateProductPageQuantityUI, 0);
      }
    });
    productForm.addEventListener("input", e => {
      if (e.target.matches(".quantity-selector__input")) {
        _updateProductPageQuantityUI();
      }
    });
    productForm.addEventListener("variant:change", () => {
      setTimeout(_updateProductPageQuantityUI, 50);
    });
  }
}

function _initializeShopifyErrorHook() {
  const originalOnError = window.Shopify.onError;

  window.Shopify.onError = function (XMLHttpRequest, textStatus) {
    // We still want the theme's original error handler to run.
    if (typeof originalOnError === "function") {
      originalOnError(XMLHttpRequest, textStatus);
    }
  };
}

// --- Main Initializer ---
function initializeCustomLogic() {
  initializeProductPageQuantityClamping();
  initializeNativeBannerFix();
  _initializeCartDrawerObserver();
  _initializeShopifyErrorHook();

  // The condition is now inside the handler, so we can call it directly.
  setupCartQuantityErrorHandler();

  /* ------------------ CART PAGE ------------------ */
  if (document.body.classList.contains("template-cart")) {
    clampQuantitiesAndButtons();
    attachCartButtonEvt();

    const cartContainer = document.querySelector(".cartitems--container");
    // Fix: Add a delegated listener for real-time quantity input changes.
    // This will handle clamping and volume price updates for all items, including those added via AJAX.
    if (cartContainer && !cartContainer.hasAttribute("data-input-handler-attached")) {
      cartContainer.addEventListener("input", e => {
        if (e.target.matches(".quantity-selector__input")) {
          // 1. Re-run the theme's clamping logic as the user types.
          clampQuantitiesAndButtons();

          // 2. Re-run the volume price highlighting.
          if (typeof window.highlightCurrentTier === "function") {
            // Calculate total cart quantity and update all grids
            let totalQty = 0;

            if (typeof window.getTotalCartItemCount === "function") {
              totalQty = window.getTotalCartItemCount();
            } else {
              // Fallback: calculate manually if function is not available
              document.querySelectorAll(".cart-item").forEach(item => {
                const qtyInput = item.querySelector(".quantity-selector__input");
                if (qtyInput) {
                  const qty = parseInt(qtyInput.value, 10) || 0;
                  totalQty += qty;
                }
              });
            }

            document.querySelectorAll(".cart-item").forEach(cartItem => {
              const grid = cartItem.querySelector(".b2b-pricing-grid");
              if (grid) {
                window.highlightCurrentTier(grid, totalQty);
              }
            });
          }
        }
      });
      cartContainer.setAttribute("data-input-handler-attached", "true");
    }
  }
}

function setupCartQuantityErrorHandler() {
  // This listener is attached to the document body, so it will always be active.
  document.body.addEventListener(
    "click",
    e => {
      // We only proceed if the click is actually on the cart page.
      if (!e.target.closest(".template-cart")) {
        return;
      }

      // And we only care about clicks on a plus button.
      const plusButton = e.target.closest("[data-quantity-plus]");
      if (!plusButton) {
        return;
      }

      const selector = plusButton.closest("[data-quantity-selector]");

      // And only for specifically flagged allocated products.
      if (!selector || !selector.hasAttribute("data-is-allocated-limit")) {
        return;
      }

      const input = selector.querySelector("input");
      const maxAttr = input.getAttribute("max");
      const max = maxAttr ? parseInt(maxAttr, 10) : null;
      const current = parseInt(input.value, 10);

      if (max !== null && current >= max) {
        // Stop the original click action to prevent quantity increase.
        e.stopPropagation();
        e.preventDefault();

        const errorMessage = selector.querySelector("[data-quantity-error-message]");
        const wrapper = selector.closest(".cart-item--qty-block");

        if (errorMessage && wrapper) {
          errorMessage.classList.add("show");
          wrapper.classList.add("has-active-error");
          setTimeout(() => {
            errorMessage.classList.remove("show");
            wrapper.classList.remove("has-active-error");
          }, 5000);
        }
      }
    },
    true
  );
}

// Restore the essential triggers for all custom logic.
document.addEventListener("DOMContentLoaded", initializeCustomLogic);
document.addEventListener("shopify:section:load", initializeCustomLogic);

document.addEventListener("cart:updated", () => {
  if (document.body.classList.contains("template-cart")) {
    clampQuantitiesAndButtons();
  }
});

/* ------------------------------------------------------------------
   Allocation helper – UNIFIED detection of products under allocation
   ------------------------------------------------------------------ */

function isAllocated(node) {
  if (!node) return false;
  /* we go up to the first ancestor carrying one of the three attributes */
  const el = node.closest("[data-under-allocation],[data-allocated],[data-is-allocated-limit]");
  if (!el) return false;
  return (
    el.dataset.underAllocation === "true" ||
    el.dataset.allocated === "true" ||
    el.hasAttribute("data-is-allocated-limit") ||
    el.dataset.paradisDugas === "true"
  );
}

/*
  const CompanyDataCache = {
    _promise: null,
    _data: null,

    async get() {
      // Si on a déjà la donnée, on la retourne
      if (this._data) {
        return this._data;
      }

      // Si une requête est en cours, on attend son résultat
      if (this._promise) {
        return this._promise;
      }

      // Sinon, on lance la requête et on la met en cache
      this._promise = window.Shopify.custom.api.account.getCompany()
        .then(res => {
          this._data = res;
          this._promise = null;
          return res.json();
        })
        .catch(err => {
          console.error('Error fetching company data:', err);
          this._promise = null;
          throw err;
        });

      return this._promise;
    },

    // Méthode pour invalider le cache si nécessaire
    invalidate() {
      this._data = null;
      this._promise = null;
    }
  };
*/

// window.addEventListener("custom-api:authenticated", () => {
//   window.customApiReady = true;
// });

class qttSelector2 extends HTMLElement {
  constructor() {
    super();

    this.minus = this.querySelector(".quantity-selector--btn-minus");
    this.plus = this.querySelector(".quantity-selector--btn-plus");
    this.input = this.querySelector("input");

    this.min = parseInt(this.input.min);
    this.max = parseInt(this.input.max);

    if (!this.minus || !this.plus || !this.input) return;

    this.input.addEventListener("change", e => this.change(e));
    this.minus.addEventListener("click", e => this.change(e, -1));
    this.plus.addEventListener("click", e => this.change(e, 1));

    this.input.addEventListener("keydown", e => {
      if (e.key === "Enter") {
        e.preventDefault();
        this.input.blur();
      }
    });

    this.getNewValue();

    // if(window.customApiReady == true) this.loadCompanyData();
    // else window.addEventListener("custom-api:authenticated", () => this.loadCompanyData());
  }

  /*
    async loadCompanyData() {
      try {
        const companyData = await CompanyDataCache.get();

        this.alreadyOrdered = 0;
        companyData.orders.forEach(o => {
          o.lineItems.forEach(i => {
            if(!i.product.id) return;
            const id = i.variantId.split('/')[4];
            
            if(id == this.dataset.id) this.alreadyOrdered += i.quantity
          })
        })

        
      } catch (err) {
        console.error('Failed to load company data in qttSelector2:', err);
      }
    }
  */

  cartUpdate(cart) {
    if (this.dataset.context != "product") return;

    let min = 1;
    let max = 1;
    let value = 1;
    let qttInCart = 0;
    const initialMax = parseInt(this.input.dataset.initialMax);
    const varId = this.input.dataset.variantId;

    cart.items.forEach(item => {
      if (item.id == varId) qttInCart += item.quantity;
    });

    max = initialMax - qttInCart;

    if (max <= 0) {
      max = 0;
      min = 0;
      value = 0;
    }

    this.min = min;
    this.input.min = min;
    this.max = max;
    this.input.max = max;
    this.input.value = value;

    this.toggleError(value);
  }

  getNewValue(i) {
    const initial = parseInt(this.input.value);
    let newValue;

    if (i) {
      newValue = initial + i;
    } else {
      newValue = this.input.value;
    }

    this.toggleError(newValue);

    if (this.min && newValue < this.min) newValue = this.min;
    else if (this.max && newValue > this.max) newValue = this.max;
    else if (this.max == 0) newValue = 0;

    return newValue;
  }

  toggleError(value) {
    if (this.min && value < this.min) this.displayError("min");
    else if (this.max && value > this.max) this.displayError("max");
    else if (this.max == 0) this.displayError("0");
    else {
      this.hideError();
      this.disable(false);
    }
  }

  change(e, i) {
    if (e) e.preventDefault();

    const newValue = this.getNewValue(i);

    this.input.value = newValue;

    if (this.dataset.context === "cart" || this.dataset.context === "cart-drawer") {
      clearTimeout(this._updateTimeout);
      this._updateTimeout = setTimeout(() => {
        updateCart(this.dataset.id, newValue);
      }, 500);
    }
  }

  displayError(m) {
    const el = this.querySelector(".error");
    if (el) el.style.display = "block";

    if (m == "min") {
      if (el) el.textContent = `Vous ne pouvez pas ajouter moins de ${this.min} articles au panier.`;
      this.disable(false);
    } else if (m == "max") {
      if (el) el.textContent = `Vous ne pouvez pas ajouter plus de ${this.max} articles au panier.`;
      this.disable(false);
    } else if (m == "0") {
      if (el) el.textContent = `Vous avez atteint la quantité maximale d'article dans le panier.`;
      this.disable(true);
    }
  }

  hideError() {
    const el = this.querySelector(".error");
    if (!el) return;
    el.style.display = "none";
  }

  disable(state) {
    let btn;
    if (this.dataset.context == "product") {
      btn = this.closest(".product-form__action-wrapper").querySelector(".product-form--atc-button");
    } else if (this.dataset.context == "predictive-search") {
      btn = this.closest(".search-flydown--product-actions").querySelector(".add-to-cart-btn");
    }

    if (btn && state) {
      btn.classList.add("no-purchase");
      btn.setAttribute("disabled", "disabled");
    } else if (btn && !state) {
      btn.classList.remove("no-purchase");
      btn.removeAttribute("disabled");
    }
  }
}
customElements.define("quantity-selector2", qttSelector2);

function updateCart(id, qtt) {
  const section =
    document.querySelector(".cartitems--container") || document.querySelector(".custom-cart-drawer__items");
  if (section) section.classList.add("loading");

  fetch("/cart/change.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ quantity: qtt, id: id }),
  });
}

function updateShopifyCart() {
  fetch("/cart.js")
    .then(response => response.json())
    .then(cart => {
      Shopify.cart = cart;
      document.dispatchEvent(new CustomEvent("cart:newcart", { detail: { cart: cart } }));
    });
}

Shopify.cart = JSON.parse(document.querySelector("#cart-inital-data").textContent);
document.addEventListener("cart:updated", () => updateShopifyCart());

document.addEventListener("cart:newcart", () => {
  const cart = Shopify.cart;
  let appliedPromos = "";
  cart.items.forEach(item =>
    item.discounts.forEach(discount => (appliedPromos = appliedPromos + "///" + item.handle + "::" + discount.title))
  );
  document.querySelector("#cart-applied-promos").value = appliedPromos;
});

document.addEventListener("cart:newcart", e => {
  document.querySelectorAll("quantity-selector2").forEach(el => el.cartUpdate(e.detail.cart));
});

/* Ecouteur d'evenements */
/*
const _dispatch = EventTarget.prototype.dispatchEvent;

const ignore = new Set(["pointermove", "mousemove", "mouseover"]);

EventTarget.prototype.dispatchEvent = function(event) {
  if (!ignore.has(event.type)) {
    console.log("[EVENT] : ", event.type);
    console.log("Target : ", this);
  }
  return _dispatch.call(this, event);
};
*/

// eslint-disable-next-line no-redeclare
const showNotif = function (msg, state) {
  const container = document.querySelector("#notif");
  if (!container) return;
  const el = document.createElement("div");
  el.classList.add(state);

  const span = document.createElement("span");
  span.textContent = msg;

  el.appendChild(span);

  container.appendChild(el);

  setTimeout(() => {
    el.remove();
  }, 10000);
};

document.addEventListener("cart:updated", async () => {
  const url = "?section_id=custom-cart-drawer";
  const res = await fetch(url, { headers: { Accept: "text/html" } });
  const doc = new DOMParser().parseFromString(await res.text(), "text/html");

  const newDOM = doc.querySelector(".custom-cart-drawer__items");
  const oldDOM = document.querySelector(".custom-cart-drawer__items");

  if (oldDOM && newDOM) oldDOM.innerHTML = newDOM.innerHTML;

  oldDOM.classList.remove("loading");
});

const stockAlert = {
  toggle: function (btn, id) {
    if (btn.classList.contains("save-true")) stockAlert.remove(btn, id);
    else if (btn.classList.contains("save-false")) stockAlert.add(btn, id);
    else showNotif("Une erreur est survenue, merci de réessayer dans quelques instants", "error");
  },
  add: async function (btn, id) {
    btn.classList.add("loading");

    try {
      const res = await window.Shopify.custom.api.stockAlert.subscribe(id);

      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText} pour ${id}`);

      showNotif("L'alerte a bien été enregistrée", "success");

      btn.classList.remove("loading", "save-false");
      btn.classList.add("save-true");
    } catch (err) {
      console.error(err);
      showNotif("Une erreur est survenue, merci de réessayer", "error");
    }
  },
  remove: async function (btn, id) {
    btn.classList.add("loading");

    try {
      const res = await window.Shopify.custom.api.stockAlert.unsubscribe(id);

      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText} pour ${id}`);

      showNotif("L'alerte a bien été supprimée", "success");

      btn.classList.remove("loading", "save-true");
      btn.classList.add("save-false");
      if (btn.classList.contains("unsubscribe")) {
        btn.closest(".product").remove();
      }
    } catch (err) {
      console.error(err);
      showNotif("Une erreur est survenue, merci de réessayer", "error");
    }
  },
};

window.addEventListener("custom-api:credit-check", e => {
  creditCheckLoading(e.detail);
});

function updateCreditCheckLayout(messages) {
  const cartContainer = document.querySelector(".cart--section .credit-check-messages .messages");
  const globalContainer = document.querySelector("#credit-check-messages");

  const existingStockMessages = cartContainer ? Array.from(cartContainer.querySelectorAll(".stock-check-message")) : [];

  if (cartContainer) {
    cartContainer.innerHTML = "";
    existingStockMessages.forEach(el => cartContainer.appendChild(el));
  }
  if (globalContainer) globalContainer.innerHTML = "";

  messages.forEach(mes => {
    const el = document.createElement("div");
    const span = document.createElement("span");
    el.classList.add("el", mes.severity);
    span.textContent = mes.message;
    el.appendChild(span);

    if (mes.type === "global" && globalContainer) globalContainer.appendChild(el);
    else if (mes.type === "cart-checkout" && cartContainer) cartContainer.appendChild(el);
  });
}

function creditCheckLoading(messages) {
  const cartButton = document.querySelector("#sidebar-checkout-button");

  let disabled = false;

  updateCreditCheckLayout(messages);

  messages.forEach(mes => {
    if (mes.type == "global") disabled = true;
  });

  if (!cartButton) return;
  cartButton.classList.remove("credit-check-loading");
  if (disabled == true && cartButton) cartButton.classList.add("credit-check-blocked");
}

function updateStockCheckLayout(unavailableItems) {
  const cartContainer = document.querySelector(".cart--section .credit-check-messages .messages");

  if (!cartContainer) return;

  const existingStockMessages = cartContainer.querySelectorAll(".stock-check-message");
  existingStockMessages.forEach(el => el.remove());

  if (unavailableItems.length === 0) return;

  unavailableItems.forEach(item => {
    const el = document.createElement("div");
    el.classList.add("el", "error", "stock-check-message");
    const span = document.createElement("span");
    span.textContent = `${item.productTitle} n'est plus disponible en quantité suffisante.`;
    el.appendChild(span);
    cartContainer.appendChild(el);
  });
}

function attachCartButtonEvt() {
  const cartButton = document.querySelector("#sidebar-checkout-button");
  const cartContainer = document.querySelector(".cart--section .credit-check-messages .messages");

  if (!cartButton) return;

  cartButton.addEventListener("click", async e => {
    console.log("cartButton clicked");
    if (window._skipCreditCheckSubmit) {
      window._skipCreditCheckSubmit = false;
      return;
    }
    if (e.isTrusted) e.preventDefault();
    cartButton.classList.add("credit-check-loading");
    const cart = window.Shopify?.cart;

    // TODO: Uncomment this when the stock check API is ready.
    let hasStockIssues = false;
    if (cart?.items && Shopify.custom?.api?.stockCheck) {
      const itemsToCheck = cart.items
        .filter(item => item.sku)
        .map(item => ({
          sku: item.sku,
          quantity: item.quantity,
        }));

      if (itemsToCheck.length > 0) {
        try {
          const stockResults = await Shopify.custom.api.stockCheck.get(itemsToCheck);
          const unavailableItems = stockResults
            .filter(result => !result.available)
            .map(result => {
              const cartItem = cart.items.find(item => item.sku === result.sku);
              return {
                sku: result.sku,
                productTitle: cartItem?.product_title || cartItem?.title || result.sku,
              };
            });

          if (unavailableItems.length > 0) {
            hasStockIssues = true;
            updateStockCheckLayout(unavailableItems);
            cartContainer.classList.add("active");
          } else {
            updateStockCheckLayout([]);
          }
        } catch (error) {
          console.error("Stock check failed:", error);
        }
      }
    }

    if (hasStockIssues) {
      cartButton.classList.remove("credit-check-loading");
      return;
    }

    const cartPrice = Math.round((cart?.total_price ?? 0) * 1.2) / 100;

    const messages = await Shopify.custom.api.creditCheck.get(cartPrice, { forceRefresh: true });

    if (messages.length > 0) {
      updateCreditCheckLayout(messages);
      cartContainer.classList.add("active");
    } else if (document.body.classList.contains("multiple-locations")) {
      // Disable the location picker for now because it's not working properly.
      // Sometimes, after the location is choosen, the user is redirected to the store instead of the checkout page.
      // Shopify.custom.accountManager.pickLocation(true);
      window._skipCreditCheckSubmit = true;
      cartButton.click();
    } else {
      window._skipCreditCheckSubmit = true;
      cartButton.click();
    }

    cartButton.classList.remove("credit-check-loading");
  });
}

async function creditCheckCartReload() {
  const cartContainer = document.querySelector(".cartitems--container");
  if (!cartContainer) return;

  let messages = [];
  try {
    const cart = window.Shopify?.cart;
    const cartPrice = Math.round((cart?.total_price ?? 0) * 1.2) / 100;
    messages = (await Shopify.custom.api.creditCheck.get(cartPrice)) || [];
  } catch (error) {
    console.error(error);
  }

  creditCheckLoading(messages);
  attachCartButtonEvt();
}

function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length));
  }
  return null;
}

document.addEventListener("DOMContentLoaded", () => {
  customerNotificationsSort();
});

function customerNotificationsSort() {
  const popup = document.querySelector(".notifications-popup");
  const menuEls = popup.querySelectorAll(".menu--el");
  const notifications = popup.querySelectorAll(".notification");
  let activeFilter = "Toutes";

  menuEls.forEach(el => {
    el.addEventListener("click", () => {
      activeFilter = el.textContent;
      menuEls.forEach(el => el.classList.remove("active"));
      el.classList.add("active");

      let toDisplay;

      if (activeFilter === "Toutes") toDisplay = notifications;
      else if (activeFilter === "Produits")
        toDisplay = popup.querySelectorAll(
          '[data-type="PRODUCT_IN_STOCK"], [data-type="ALLOCATION_REQUEST_APPROVED"], [data-type="ALLOCATION_REQUEST_PARTIALLY_APPROVED"], [data-type="ALLOCATION_REQUEST_REJECTED"]'
        );
      else if (activeFilter === "Documents") toDisplay = popup.querySelectorAll('[data-type="INVOICE_AVAILABLE"]');
      else if (activeFilter === "Expédition") toDisplay = popup.querySelectorAll('[data-type="ORDER_SHIPPED"]');

      notifications.forEach(notif => (notif.style.display = "none"));
      toDisplay.forEach(notif => (notif.style.display = "flex"));
    });
  });
}

document.querySelectorAll(".notifications-popup .notification").forEach(notif => {
  notif.addEventListener("click", e => {
    e.preventDefault();
    notif.classList.add("loading");
    try {
      Shopify.custom.api.notification.markAsSeen(notif.dataset.id);
    } catch (err) {
      console.error(err);
    }
    notif.classList.remove("loading");
    window.location.href = notif.dataset.redirect;
  });
});
